import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Chip, Paper, Divider, styled, Stack
} from '@mui/material';
import { CheckCircle, Cancel, Add, Refresh } from '@mui/icons-material';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const T = {
  amber: '#C2823C', amberLight: '#E8A85A', sage: '#6B8C6E',
  rose: '#C4706A', ink: '#1a1510', inkMid: '#4a3f30', inkLight: '#8a7055',
  border: 'rgba(194,130,60,0.18)', ivory: '#FFFEFB',
};

const StatusChip = ({ status }) => {
  const map = {
    pending:    { label: 'Pending',    bg: '#FFF8F0', color: T.amber },
    uploaded:   { label: 'Uploaded',   bg: '#FFF3E0', color: '#E65100' },
    reviewed:   { label: 'Reviewed',   bg: '#E8F5E9', color: T.sage },
    regenerated:{ label: 'Regenerated',bg: '#E3F2FD', color: '#1565C0' },
  };
  const s = map[status] || map.pending;
  return (
    <Chip label={s.label} size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, border: `1px solid ${s.color}30`, fontSize: '0.72rem' }} />
  );
};

export default function PhotoRequestReview({ eventId, userId, eventName, onRegenerateVideo,onPhotosAdded }) {
  const [requests,     setRequests]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [sendOpen,     setSendOpen]     = useState(false);
  const [guestEmail,   setGuestEmail]   = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [sending,      setSending]      = useState(false);
  const [actionLoading,setActionLoading]= useState('');
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  const token = localStorage.getItem('authToken');
//   const headers = { Authorization: token?.trim() };
  const headers = { Authorization: `Bearer ${token?.trim()}` };

  const load = () => {
    setLoading(true);
    axios.get(`${BASE_URL}/photo-requests?event_id=${eventId}`, { headers })
      .then(r => setRequests(r.data.requests || []))
      .catch(() => setError('Failed to load photo requests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [eventId]);

  const handleSendRequest = async () => {
    if (!guestEmail.trim()) return;
    setSending(true); setError('');
    try {
      await axios.post(`${BASE_URL}/photo-requests`, {
        event_id: eventId, event_name: eventName,
        guest_email: guestEmail, message: guestMessage,
      }, { headers });
      setSuccess(`Upload link sent to ${guestEmail}!`);
      setSendOpen(false); setGuestEmail(''); setGuestMessage('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send request');
    } finally { setSending(false); }
  };
// 1. Fix handleReview — send raw photo_url (path), not the display URL
const handleReview = async (requestId, photoUrl, action) => {
    setActionLoading(`${requestId}-${photoUrl}-${action}`);
    try {
      await axios.put(
        `${BASE_URL}/photo-requests/${requestId}/review`,
        { photo_url: photoUrl, action },   // action comes from the parameter, not hardcoded
        { headers: { Authorization: `Bearer ${token}` } }
      );
      load();
    } catch { setError('Review action failed'); }
    finally { setActionLoading(''); }
  };

  const handleAddToEvent = async (requestId) => {
    setActionLoading(`add-${requestId}`);
    try {
      const r = await axios.post(`${BASE_URL}/photo-requests/${requestId}/add-to-event`, {}, { headers });
      setSuccess(r.data.message);
      load();
      // ✅ Tell the parent photos were added — parent opens EventForm
      if (onPhotosAdded) onPhotosAdded(eventId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add photos to event');
    } finally { setActionLoading(''); }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.4rem', color: T.ink }}>
          Photo Requests
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={load} startIcon={<Refresh sx={{ fontSize: 16 }} />}
            sx={{ color: T.inkLight, textTransform: 'none', fontSize: '0.85rem', borderRadius: '50px', border: `1px solid ${T.border}`, px: 2 }}>
            Refresh
          </Button>
          <Button onClick={() => setSendOpen(true)} startIcon={<Add sx={{ fontSize: 16 }} />}
            sx={{ background: `linear-gradient(135deg, ${T.amber}, ${T.amberLight})`, color: '#fff', textTransform: 'none', fontSize: '0.85rem', borderRadius: '50px', px: 2.5, fontWeight: 600, '&:hover': { opacity: 0.9 } }}>
            Request Photos
          </Button>
        </Stack>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Send request form */}
      {sendOpen && (
        <Paper elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: '16px', p: 3, mb: 3, background: '#FFF8F0' }}>
          <Typography sx={{ fontWeight: 600, color: T.ink, mb: 2, fontSize: '0.95rem' }}>
            Send Upload Request
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)}
              placeholder="Guest email address"
              style={{ padding: '11px 14px', borderRadius: '10px', border: `1px solid rgba(194,130,60,0.3)`, fontFamily: "'DM Sans'", fontSize: '0.9rem', background: T.ivory, color: T.ink, outline: 'none' }} />
            <textarea value={guestMessage} onChange={e => setGuestMessage(e.target.value)}
              placeholder="Optional message to guest…"
              rows={3}
              style={{ padding: '11px 14px', borderRadius: '10px', border: `1px solid rgba(194,130,60,0.3)`, fontFamily: "'DM Sans'", fontSize: '0.9rem', background: T.ivory, color: T.ink, outline: 'none', resize: 'vertical' }} />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={() => setSendOpen(false)} sx={{ color: T.inkLight, textTransform: 'none', borderRadius: '50px', border: `1px solid ${T.border}`, px: 2.5, fontSize: '0.85rem' }}>
                Cancel
              </Button>
              <Button onClick={handleSendRequest} disabled={sending || !guestEmail.trim()}
                sx={{ background: `linear-gradient(135deg, ${T.amber}, ${T.amberLight})`, color: '#fff', textTransform: 'none', borderRadius: '50px', px: 2.5, fontWeight: 600, fontSize: '0.85rem', '&:hover': { opacity: 0.9 }, '&:disabled': { opacity: 0.5 } }}>
                {sending ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
                {sending ? 'Sending…' : 'Send Invite'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Requests list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: T.amber }} />
        </Box>
      ) : requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: T.inkLight }}>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '1.1rem', mb: 1 }}>
            No photo requests yet
          </Typography>
          <Typography sx={{ fontSize: '0.88rem' }}>
            Click "Request Photos" to invite guests to upload their photos
          </Typography>
        </Box>
      ) : (
        requests.map(req => (
          <Paper key={req.request_id} elevation={0}
            sx={{ border: `1px solid ${T.border}`, borderRadius: '16px', p: 2.5, mb: 2.5, background: T.ivory }}>

            {/* Request header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.95rem' }}>
                  {req.guest_email}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: T.inkLight, mt: 0.3 }}>
                  {req.upload_count} photo{req.upload_count !== 1 ? 's' : ''} uploaded
                  · {req.pending_count} pending review
                  · {req.approved_count} approved
                </Typography>
              </Box>
              <StatusChip status={req.status} />
            </Box>

            {/* Photos grid */}
            {req.uploaded_photos?.length > 0 && (
              <>
                <Divider sx={{ borderColor: T.border, mb: 2 }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: T.inkLight, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>
                  Uploaded Photos
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1.5, mb: 2 }}>
                  {req.uploaded_photos.map((photo, idx) => {
                    const isApproved = photo.status === 'approved';
                    const isRejected = photo.status === 'rejected';
                    return (
                      <Box key={idx} sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '1', border: `2px solid ${isApproved ? T.sage : isRejected ? T.rose : T.border}` }}>
                          <img
                            src={`${BASE_URL}/uploaded-photos/${photo.photo_url}`}
                            alt={photo.original_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />

                        {/* Status overlay */}
                        {(isApproved || isRejected) && (
                          <Box sx={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isApproved ? 'rgba(107,140,110,0.35)' : 'rgba(196,112,106,0.35)',
                          }}>
                            {isApproved
                              ? <CheckCircle sx={{ color: '#fff', fontSize: 28 }} />
                              : <Cancel      sx={{ color: '#fff', fontSize: 28 }} />}
                          </Box>
                        )}

                        {/* Action buttons for pending */}
                        {!isApproved && !isRejected && (
                          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 0.5, p: 0.8, background: 'linear-gradient(transparent, rgba(0,0,0,0.55))' }}>
                            <Button size="small" onClick={() => handleReview(req.request_id, photo.photo_url, 'approve')}
                              disabled={!!actionLoading}
                              sx={{ flex: 1, minWidth: 0, py: 0.4, borderRadius: '6px', background: T.sage, color: '#fff', fontSize: '0.65rem', fontWeight: 700, textTransform: 'none', '&:hover': { background: '#5a7b5d' }, '&:disabled': { opacity: 0.5 } }}>
                              ✓
                            </Button>
                            <Button size="small" onClick={() => handleReview(req.request_id, photo.photo_url, 'reject')}
                              disabled={!!actionLoading}
                              sx={{ flex: 1, minWidth: 0, py: 0.4, borderRadius: '6px', background: T.rose, color: '#fff', fontSize: '0.65rem', fontWeight: 700, textTransform: 'none', '&:hover': { background: '#b55e58' }, '&:disabled': { opacity: 0.5 } }}>
                              ✗
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}

            {req.approved_count > 0 && req.status !== 'regenerated' && (
              <Button
                onClick={() => handleAddToEvent(req.request_id)}
                disabled={actionLoading === `add-${req.request_id}`}
                startIcon={actionLoading === `add-${req.request_id}` ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Add sx={{ fontSize: 16 }} />}
                sx={{ background: `linear-gradient(135deg, ${T.amber}, ${T.amberLight})`, color: '#fff', textTransform: 'none', borderRadius: '50px', px: 2.5, fontWeight: 600, fontSize: '0.85rem', '&:hover': { opacity: 0.9 }, '&:disabled': { opacity: 0.5 } }}>
                Add {req.approved_count} Photo{req.approved_count > 1 ? 's' : ''} to Event & Regenerate
              </Button>
            )}

            {req.status === 'reviewed' && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ borderRadius: '10px', py: 0.5 }}>
                Photos added to event — click the video generate button to create an updated video.
              </Alert>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
}